// WO-33 — Piece 1: the welcome window (spec §2). First login of a new account,
// once. Radix Dialog gives the focus trap and Esc for free.
import React, { useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useOnboarding } from './OnboardingContext';
import { onboardingCopy } from './copy';
import { btnPrimary, btnSecondary, fontStyle } from './styles';

const GOALS = ['tenders', 'projects_news', 'visibility'];

export default function WelcomeDialog() {
  const onb = useOnboarding();
  const [goal, setGoal] = useState(null);
  const c = onboardingCopy.en.welcome;

  if (!onb.showWelcome) return null;

  // ✕, Esc and "Just looking around" all mean: browse, checklist minimised.
  const browse = () => onb.chooseGoal('browse', 'minimised');

  return (
    <DialogPrimitive.Root open onOpenChange={(open) => { if (!open) browse(); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=open]:fade-in-0"
          style={{ animationDuration: '150ms' }}
        />
        <DialogPrimitive.Content
          dir="ltr"
          lang="en"
          aria-describedby="onb-welcome-intro"
          data-testid="onboarding-welcome"
          // Only ✕, Esc and "Just looking around" close it (spec §2) — a click
          // elsewhere (e.g. on the cookie banner) must not count as an answer.
          onInteractOutside={(e) => e.preventDefault()}
          className="fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-[560px] -translate-x-1/2 -translate-y-1/2 max-h-[90vh] overflow-y-auto rounded-lg border border-[#E2E8F0] dark:border-border bg-white dark:bg-card p-6 shadow-[0_1px_3px_rgba(0,0,0,.08)] text-left focus:outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0"
          style={{ ...fontStyle, animationDuration: '150ms' }}
        >
          <DialogPrimitive.Close
            className="absolute right-4 top-4 rounded-md p-1 text-[#4A5568] dark:text-muted-foreground hover:bg-black/5 dark:hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#217922]"
            aria-label={c.close}
            data-testid="onboarding-welcome-close"
          >
            <X className="h-5 w-5" />
          </DialogPrimitive.Close>

          <DialogPrimitive.Title className="text-xl font-semibold text-[#0F2D4E] dark:text-white pr-8">
            {c.title}
          </DialogPrimitive.Title>
          <p id="onb-welcome-intro" className="mt-2 text-sm text-[#4A5568] dark:text-muted-foreground">
            {c.intro}
          </p>

          <div role="radiogroup" aria-label={c.title} className="mt-4 space-y-2">
            {GOALS.map((g) => {
              const selected = goal === g;
              return (
                <button
                  key={g}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => setGoal(g)}
                  data-testid={`onboarding-goal-${g}`}
                  className={`w-full rounded-lg border p-4 text-left transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#217922] ${
                    selected
                      ? 'border-[#217922] dark:border-[#3BB34A] bg-[#217922]/5 dark:bg-[#3BB34A]/10'
                      : 'border-[#E2E8F0] dark:border-border hover:border-[#217922]/60'
                  }`}
                >
                  <span className="block text-sm font-semibold text-[#0F2D4E] dark:text-white">{c.options[g].title}</span>
                  <span className="block mt-0.5 text-sm text-[#4A5568] dark:text-muted-foreground">{c.options[g].line}</span>
                </button>
              );
            })}
          </div>

          <div className="mt-6 flex flex-col-reverse sm:flex-row sm:items-center gap-2">
            <button
              type="button"
              onClick={browse}
              className="text-sm text-[#4A5568] dark:text-muted-foreground underline-offset-2 hover:underline sm:mr-auto min-h-[40px] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#217922] rounded-md px-2"
              data-testid="onboarding-just-looking"
            >
              {c.justLooking}
            </button>
            <button
              type="button"
              onClick={() => onb.chooseGoal(goal, 'tour')}
              className={btnSecondary}
              data-testid="onboarding-show-me-around"
            >
              {c.showMeAround}
            </button>
            <button
              type="button"
              disabled={!goal}
              onClick={() => onb.chooseGoal(goal, 'checklist')}
              className={btnPrimary}
              data-testid="onboarding-get-started"
            >
              {c.getStarted}
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
